import PropTypes from 'prop-types';
import { NewBookingDeliveryPropType } from 'common/prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import usePrevious from 'common/usePrevious';
import I18n from 'i18n-js';
import _get from 'lodash/get';
import _max from 'lodash/max';
import { Button, Checkbox, Spin, Tooltip } from 'grab-ui';
import calcPrice from 'common/utils/calc-price';
import formatCurrency from 'common/utils/format-currency';
import { userInfoSelector, enableMSDSelector } from 'selectors/app-selector';
import { deliverySelector } from 'selectors/new-booking-selectors';
import optimizeRouteIcon from 'public/icons/optimize-route.svg';
import circledCheckMark from 'public/icons/circled-check-mark.svg';
import greenDownArrow from 'public/icons/green-down-arrow.svg';
import NewBookingDuck from 'ducks/new-booking-duck';
import ScribeDuck from 'ducks/scribe-duck';
import DeliveryContext from '../../common/context';
import AddressSelector from './address-selector';
import styles from './styles.less';
import {
  useLazyLoadLottie,
  ANIMATION_KEYS,
} from '../../../../../hooks/useLazyLoadLottie';

const DropOffAddressSelector = ({
  dispatch,
  deliveryID,
  delivery,
  onClose,
  enableMSD,
}) => {
  const version = _get(delivery, 'version');
  const isRoundTrip = _get(delivery, 'detail.isRoundTrip', false);
  const autoRoundTrip = _get(delivery, 'detail.autoRoundTrip', false);
  const pickUpStep = _get(delivery, 'detail.pickUp');
  const dropOffSteps = _get(delivery, 'detail.dropOffs');

  const onDropOffAddressChange = useCallback(
    (newDeliverySteps, forceRefresh) => {
      // when it's not roundTrip, and the last delivery step poi === pick up step poi
      let needForceRefresh = forceRefresh;
      if (
        _get(newDeliverySteps, 'length', 0) > 1 &&
        !isRoundTrip &&
        _get(pickUpStep, 'poi.id') &&
        _get(pickUpStep, 'poi.id') ===
          _get(newDeliverySteps[newDeliverySteps.length - 1], 'poi.id')
      ) {
        dispatch(
          NewBookingDuck.creators.setDropOffs(
            deliveryID,
            newDeliverySteps.slice(0, newDeliverySteps.length - 1),
          ),
        );
        dispatch(NewBookingDuck.creators.setRoundTrip(deliveryID, true, true));
        needForceRefresh = true;
      } else {
        dispatch(
          NewBookingDuck.creators.setDropOffs(
            deliveryID,
            newDeliverySteps || [],
          ),
        );
      }
      if (needForceRefresh) {
        dispatch(NewBookingDuck.creators.refreshDeliveryIDVersion(deliveryID));
      }
    },
    [dispatch, pickUpStep, deliveryID, isRoundTrip],
  );

  const selectedServiceType = _get(
    delivery,
    'detail.configuration.serviceType',
    '',
  );
  const serviceType = _get(delivery, 'config.serviceTypes', []).find(
    st => st.id === selectedServiceType,
  );

  const maxDropOff = useMemo(
    () => {
      if (serviceType) {
        return _get(serviceType, 'maxDropOff.value', 1);
      }
      const maxOfMax = _max(
        _get(delivery, 'config.serviceTypes', []).map(st =>
          _get(st, 'maxDropOff.value', 0),
        ),
      );
      if (maxOfMax > 0) {
        return maxOfMax;
      }
      return 20;
    },
    [serviceType, delivery],
  );
  const maxDropOffErrorHint = _get(
    serviceType,
    'maxDropOff.errorMessage.title',
    I18n.t('v2.anytimeBooking.maxDropOffError'),
  );

  const quote = _get(delivery, 'quote');
  const fetchingQuote = _get(delivery, 'fetchingQuote');
  const routeOptimization = _get(delivery, 'routeOptimization');
  const [roRottieAnimPlayed, setRoRottieAnimPlayed] = useState(false);
  const [optimizing, setOptimizing] = useState(false);

  const onToggleRoundTrip = useCallback(
    () => {
      dispatch(NewBookingDuck.creators.setRoundTrip(deliveryID, !isRoundTrip));
    },
    [dispatch, deliveryID, isRoundTrip],
  );

  const onEnableRouteOptimization = useCallback(
    () => {
      dispatch(NewBookingDuck.creators.enableRouteOptimization(deliveryID));
      dispatch(
        ScribeDuck.creators.scribeExpressPOIFindShortestRoute(deliveryID),
      );
    },
    [dispatch, deliveryID],
  );
  const onRevertRouteOptimization = useCallback(
    () => {
      dispatch(NewBookingDuck.creators.revertRouteOptimization(deliveryID));
      dispatch(
        ScribeDuck.creators.scribeExpressPOIUseOriginalRoute(deliveryID),
      );
    },
    [dispatch, deliveryID],
  );

  const playLottieAnimOnce = useCallback(anim => {
    if (!anim) {
      return;
    }
    if (!window._roRottieAnimPlayed) {
      setTimeout(() => {
        window._roRottieAnimPlayed = true;
      }, 1000);
      anim.play();
    }
  }, []);

  const {
    LottieComponent: ROAnimationData,
    isLoading: isROLoading,
  } = useLazyLoadLottie(ANIMATION_KEYS.ROUTE_OPTIMISATION, {
    ref: c => playLottieAnimOnce(c),
    options: {
      loop: false,
      autoplay: false,
    },
    height: 18,
    width: 18,
  });

  const roButton = useMemo(
    () => {
      const { enabled, OptimizedSteps } = routeOptimization;
      if (
        !_get(pickUpStep, 'poi') ||
        dropOffSteps.filter(s => s.poi).length < 2
      ) {
        return undefined;
      }
      if (!enabled) {
        return (
          <Tooltip
            key={'roButton-find'}
            placement="bottomRight"
            title={I18n.t('v2.locationDrawer.tooltip.findShortestRoute')}
          >
            <Button onClick={onEnableRouteOptimization}>
              <span className={styles.icon}>
                {!window._roRottieAnimPlayed ? (
                  !isROLoading && ROAnimationData && <ROAnimationData />
                ) : (
                  <img src={optimizeRouteIcon} alt={''} />
                )}
              </span>
              {I18n.t('v2.locationDrawer.findShortestRoute')}
            </Button>
          </Tooltip>
        );
      }
      if (fetchingQuote) {
        setOptimizing(true);
        setTimeout(() => setOptimizing(false), 1000);
        return undefined;
      }
      if (OptimizedSteps) {
        return (
          <Tooltip
            key={'roButton-useOrigin'}
            placement="bottomRight"
            title={I18n.t('v2.locationDrawer.tooltip.useOriginalRoute')}
          >
            <Button onClick={onRevertRouteOptimization}>
              <span className={styles.icon}>
                <img src={optimizeRouteIcon} alt={''} />
              </span>
              {I18n.t('v2.locationDrawer.useOriginalRoute')}
            </Button>
          </Tooltip>
        );
      }
      return (
        <Tooltip
          key={'roButton-alrShortened'}
          placement="bottomRight"
          title={I18n.t('v2.locationDrawer.tooltip.alreadyShortened')}
        >
          <Button disabled className={styles.disabled}>
            <span className={styles.icon}>
              <img src={circledCheckMark} alt={''} />
            </span>
            {I18n.t('v2.locationDrawer.routeAlreadyShortened')}
          </Button>
        </Tooltip>
      );
    },
    [
      routeOptimization,
      quote,
      setOptimizing,
      roRottieAnimPlayed,
      setRoRottieAnimPlayed,
      playLottieAnimOnce,
      fetchingQuote,
      pickUpStep,
      dropOffSteps,
      onEnableRouteOptimization,
      onRevertRouteOptimization,
    ],
  );

  const rtCheckbox = useMemo(
    () => {
      // Don't show round trip checkbox when enableMSD is false
      if (!enableMSD) {
        return undefined;
      }

      const validDropOffSteps = dropOffSteps.filter(s => s.poi);
      if (!_get(pickUpStep, 'poi') || validDropOffSteps.length < 1) {
        return undefined;
      }
      const disabled = validDropOffSteps.length >= maxDropOff;
      const checkbox = (
        <Checkbox
          key={'rtCheckbox'}
          checked={isRoundTrip}
          onChange={onToggleRoundTrip}
          className={styles.rtCheckbox}
          disabled={disabled}
        >
          <span className={styles.rtLabel}>
            {I18n.t('v2.locationDrawer.roundTrip.body')}
          </span>
        </Checkbox>
      );
      if (autoRoundTrip) {
        return (
          <Tooltip
            title={I18n.t('v2.locationDrawer.roundTrip.tooltip')}
            placement={'bottomLeft'}
          >
            {checkbox}
          </Tooltip>
        );
      }
      if (disabled) {
        return (
          <Tooltip title={maxDropOffErrorHint} placement={'bottomLeft'}>
            {checkbox}
          </Tooltip>
        );
      }
      return checkbox;
    },
    [
      enableMSD,
      maxDropOff,
      pickUpStep,
      dropOffSteps,
      isRoundTrip,
      onToggleRoundTrip,
      maxDropOffErrorHint,
      autoRoundTrip,
    ],
  );

  const extraActionButtons = useMemo(() => [rtCheckbox, roButton], [
    rtCheckbox,
    roButton,
  ]);

  const {
    LottieComponent: DriverNearbyAnimationData,
    isLoading: isDriverNearbyLoading,
  } = useLazyLoadLottie(ANIMATION_KEYS.DRIVER_NEARBY, {
    options: {
      loop: true,
      autoplay: true,
    },
    height: 133,
    width: 172,
  });

  const loadingOverlay = useMemo(
    () => {
      if (optimizing) {
        return (
          <div className={styles.loadingOverlay}>
            {!isDriverNearbyLoading &&
              DriverNearbyAnimationData && <DriverNearbyAnimationData />}
            <h3>{I18n.t('v2.locationDrawer.overlayTitle')}</h3>
            <p>{I18n.t('v2.locationDrawer.overlayDesc')}</p>
          </div>
        );
      }
      return undefined;
    },
    [optimizing],
  );

  const footer = useMemo(
    () => {
      if (dropOffSteps.length < 2) {
        return undefined;
      }
      if (fetchingQuote) {
        return (
          <div className={styles.quoteFooter}>
            <span>{I18n.t('v2.anytimeBooking.estimatedPrice')}</span>
            <Spin />
          </div>
        );
      }

      const originalFare = _get(routeOptimization, 'original.fare');
      const fareLowerBound = _get(quote, 'finalFare.lowerBound', 0);
      const currency = _get(quote, 'currency');
      const amount = calcPrice(fareLowerBound, currency);
      if (!currency || !amount) {
        return undefined;
      }

      return (
        <div className={styles.quoteFooter}>
          <span>{I18n.t('v2.anytimeBooking.estimatedPrice')}</span>
          <div className={styles.stackedPrices}>
            {originalFare && (
              <span className={styles.prevQuotePrice}>
                {formatCurrency({ code: currency.code, amount: originalFare })}
              </span>
            )}
            <span className={styles.quotePrice}>
              {originalFare && <img src={greenDownArrow} alt="" />}
              {formatCurrency({ code: currency.code, amount })}
            </span>
          </div>
        </div>
      );
    },
    [dropOffSteps, fetchingQuote, routeOptimization, quote],
  );

  const routeOptimized =
    routeOptimization.enabled && !!routeOptimization.OptimizedSteps;
  const prevRouteOptimized = usePrevious(routeOptimized);
  const roChanged = routeOptimized !== prevRouteOptimized;

  if (!delivery) {
    return null;
  }

  return (
    <DeliveryContext.Provider value={delivery}>
      <AddressSelector
        key={`dropoff-${deliveryID}-${version}`}
        deliveryID={deliveryID}
        deliverySteps={dropOffSteps || null}
        readOnlyDeliveryStep={isRoundTrip ? pickUpStep : undefined}
        forceRefresh={roChanged}
        onChange={onDropOffAddressChange}
        isPickUp={false}
        onClose={onClose}
        extraActionButtons={extraActionButtons}
        overlay={loadingOverlay}
        footer={footer}
        allowMultipleAddresses={maxDropOff > 1}
        maxAddresses={isRoundTrip ? maxDropOff - 1 : maxDropOff}
        maxAddressesErrorHint={maxDropOffErrorHint}
        addAddressPlaceholder={I18n.t(
          'v2.locationDrawer.placeholder.addDropOffLocation',
        )}
      />
    </DeliveryContext.Provider>
  );
};

DropOffAddressSelector.propTypes = {
  delivery: NewBookingDeliveryPropType.isRequired,
  deliveryID: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  enableMSD: PropTypes.bool.isRequired,
};

export default connect((state, props) => ({
  userInfo: userInfoSelector(state),
  delivery: deliverySelector(state, props),
  enableMSD: enableMSDSelector(state),
}))(DropOffAddressSelector);
